import { setMockSignedIn } from '@/lib/mockSignedIn';
import { enrolledPasskeySchema, passkeySignedInSchema } from '@bp-files/client';
import type { EnrolledPasskey, EnrollPasskeyInput, PostJson } from '@bp-files/client';

/**
 * The sign-in gate's counterpart to `msal.ts`, for mocked builds.
 *
 * **The whole reason the gate is reachable in dev at all.** `VITE_USE_MOCKS`
 * builds never download MSAL — the invariant ADR-0027 rests on and ADR-0034
 * keeps — so without this the landing page could only be looked at against a
 * real tenant, which is where you cannot iterate on it. `Bootstrap` picks its
 * pair of functions once, and the dynamic `import('@/lib/msal')` still never
 * happens under mocks.
 *
 * **It deliberately never registers a session.** Under mocks `getSession()` is
 * `undefined`, which is why `AppShell` renders no header at all —
 * `smoke.spec.ts` asserts there is no `banner` landmark and `App.test.tsx`
 * asserts the banner holds no heading. On the real path `msal.ts`'s `verifyAuth`
 * does the registering; here the gate greets this session and drops it. Mock
 * mode has its own identity affordance in the environment banner's
 * `MockPersonaSwitch`.
 *
 * _Amended (ADR-0051)._ That used to hold by itself, because `canHoldSession`
 * was false in a two-origin dev build and `bpSession.ts`'s cookie branch could
 * not run. Dev is one origin now, so the same module states this as its own
 * condition — the sign-out it would otherwise synthesize revokes a row MSW
 * invented and leaves `bp.mock.signed-in` set.
 *
 * The name matches the mocked `/me` in `test/mocks/handlers.ts`, so the greeting
 * and the account menu agree the way they would in a real session.
 */

/**
 * **The MSAL pair used to be here, and M11 removed both.**
 *
 * `verifyAuth` reported a mocked Entra session and `beginSignIn` faked the
 * redirect by reloading. Neither is reachable now: the passcode is a form on
 * the landing page (SYS-7), so `apps/web` never loads `msal.js` and never asks
 * a driver whether Entra knows anybody. `MockAuth` made the pair optional for
 * exactly this, and the operator console — which still signs in by redirect —
 * still supplies them.
 *
 * What is left below is the passkey trio, which is genuinely un-mockable any
 * other way: jsdom has no `navigator.credentials`.
 */

/**
 * Enrolling a passkey with no authenticator (USR-11).
 *
 * **It skips the ceremony and keeps the network**, which is the whole shape of
 * this file's bargain. jsdom has no `navigator.credentials` and a mocked build
 * must download no WebAuthn library, so the browser half is imaginary — but MSW
 * is answering the API, so the verify call is real and the credential the
 * screen then lists is one a handler actually returned. Only the signature is
 * pretend.
 *
 * The `response` below is a shape rather than a ceremony: the mocked handler
 * never verifies it, and the real one would refuse it instantly. That asymmetry
 * is deliberate and is why this lives here and not in `@bp-files/client` — a
 * fixture belongs to the app that asserts it (ADR-0036).
 */
export function enrollPasskey(
  postJson: PostJson,
  input: EnrollPasskeyInput,
): Promise<EnrolledPasskey> {
  return postJson('/auth/passkey/register/verify', enrolledPasskeySchema, {
    label: input.label,
    sharedDevice: input.sharedDevice,
    response: { id: `mock-${String(Date.now())}`, response: { clientDataJSON: 'mock' } },
  });
}

/**
 * Always true, so the dev loop sees the "your own device" branch by default.
 *
 * The shared-workstation branch is reachable by answering the question, which
 * is the point — the answer is a person's, not a capability check's.
 */
export function platformAuthenticatorAvailable(): Promise<boolean> {
  return Promise.resolve(true);
}

/**
 * Signing in with a passkey, without one (SYS-8).
 *
 * **A reload rather than a ceremony**, which is `beginSignIn`'s bargain one
 * route over. There is no authenticator to gesture at and no cookie to be set —
 * MSW answers the verify call, and the honest imitation of "the tab comes back
 * signed in" is the same reload the passcode mock performs.
 *
 * **It does not reload, unlike `beginSignIn`**, and that difference is the real
 * one rather than a mock detail. A passkey session is a cookie, and `verifyAuth`
 * is MSAL's — it knows nothing about one. So the gate hands over directly after
 * an assertion instead of coming back through a check that would say "not
 * signed in" beside a perfectly good session. The flag is still set, so a later
 * reload finds the tab signed in.
 */
export function signInWithPasskey(postJson: PostJson): Promise<{ restricted: boolean }> {
  setMockSignedIn(true);

  return postJson('/auth/passkey/assert/verify', passkeySignedInSchema, {
    response: { id: 'mock-credential', response: { clientDataJSON: 'mock' } },
  });
}
